import { Component, EventEmitter, Output, inject, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { FilterModel } from '@core/models/filter.model';
import * as fromRoot from '@core/redux';
import * as LayoutAction from '@core/redux/layout/layout.actions';
import { ProjectFilterService } from '@core/services/project-filter.service';
import { FilterStoreService } from '@core/services/store/filter.store.service';
import { Store } from '@ngrx/store';
import { NgClickOutsideDirective } from 'ng-click-outside2';

@Component({
  selector: 'filter-popup',
  templateUrl: './filter-popup.component.html',
  styleUrls: ['./filter-popup.component.less'],
  standalone: true,
  imports: [NgClickOutsideDirective],
  providers: [FilterStoreService],
})
export class FilterPopupComponent {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly filterStoreService = inject(FilterStoreService);
  private readonly projectFilterService = inject(ProjectFilterService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  @Output() onClose = new EventEmitter<void>();

  protected readonly searchTerm = signal<string>('');
  protected readonly filters = toSignal(this.store.select(fromRoot.getFilterEntities), {
    initialValue: [] as FilterModel[],
  });
  protected readonly filteredFilters = computed(() => {
    const term = this.searchTerm().toLowerCase();
    return this.filters().filter(item => item.name.toLowerCase().includes(term));
  });

  protected onEdit(filter: FilterModel): void {
    this.filterStoreService.selectFilter(filter.id);
    this.store.dispatch(new LayoutAction.OpenSidebarFilter());
  }

  protected onDelete(filter: FilterModel): void {
    this.filterStoreService.selectFilter(null);
    this.filterStoreService.deleteFilter(filter);

    // Удаляем сохраненный фильтр проекта
    this.route.params.subscribe(params => {
      const projectId = params['projectId'];
      if (projectId) {
        this.projectFilterService.clearProjectFilter(projectId);
      }
    });
  }

  /**
   * Открыть фильтр (сайдбар)
   */
  protected onOpenSidebar(): void {
    this.store.dispatch(new LayoutAction.OpenSidebarFilter());
    this.onClose.emit();
  }

  protected onSubmitFilter(filter: FilterModel): void {
    const queryParams = this.filterStoreService.getFilterByParamsForQueryParams(filter);

    // Сохраняем фильтр для текущего проекта
    this.route.parent?.parent?.params.subscribe(params => {
      const projectId = params['projectId'];
      if (projectId) {
        this.projectFilterService.saveProjectFilter(projectId, queryParams);
      }
    });

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
    this.onClose.emit();
  }

  protected onFilterItems(target: EventTarget | null): void {
    if (!target || !(target instanceof HTMLInputElement)) {
      return;
    }
    this.searchTerm.set(target.value);
  }
}
